/**
 * @file components/ConnectionLostModal.jsx
 * @description Full-screen modal shown when the Socket.IO connection is lost
 * for longer than the configured debounce period.
 *
 * The modal is non-dismissible (no close button, no backdrop click) — the user
 * must either wait for the automatic reconnect or reload the page manually.
 * It disappears automatically when the socket reconnects.
 *
 * @param {Object}   props
 * @param {boolean}  props.show            - Whether the modal is visible.
 * @param {boolean}  [props.unrecoverable] - True once automatic retry has been
 *   abandoned (e.g. the session can no longer be silently renewed) — shows a
 *   "please reload" message instead of the reconnecting spinner.
 * @param {Function} props.onReload        - Called when the user clicks "Reload page".
 */

import React, { useEffect, useState } from 'react';

/**
 * Renders a blocking connection-lost modal with a live reconnecting animation.
 * Hides itself when `show` transitions back to false.
 */
export default function ConnectionLostModal({ show, unrecoverable, onReload }) {
  /**
   * Track dot-animation frame for the "Reconnecting..." indicator.
   * Cycles through 1–3 dots so the user can see activity.
   */
  const [dots, setDots] = useState(1);

  useEffect(() => {
    if (!show || unrecoverable) return;
    const interval = setInterval(() => {
      setDots((d) => (d % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, [show, unrecoverable]);

  if (!show) return null;

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1200 }}
        aria-hidden="true"
      />

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="conn-lost-title"
        aria-describedby="conn-lost-desc"
        style={{ zIndex: 1300 }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">

            {/* Header */}
            <div
              className="modal-header border-0"
              style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
            >
              <h5 className="modal-title d-flex align-items-center gap-2" id="conn-lost-title">
                <i className={`bi ${unrecoverable ? 'bi-exclamation-triangle-fill' : 'bi-wifi-off'} fs-5`} aria-hidden="true" />
                {unrecoverable ? 'Session Expired' : 'Connection Lost'}
              </h5>
            </div>

            {/* Body */}
            <div className="modal-body text-center py-4">
              {/* Icon */}
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: 64,
                  height: 64,
                  backgroundColor: 'var(--color-accent-3)',
                }}
                aria-hidden="true"
              >
                <i
                  className={`bi ${unrecoverable ? 'bi-arrow-clockwise' : 'bi-plug-fill'} fs-3`}
                  style={{ color: 'var(--color-primary)' }}
                />
              </div>

              {unrecoverable ? (
                <>
                  <p className="fw-semibold mb-1" id="conn-lost-desc">
                    Your session could not be renewed automatically.
                  </p>
                  <p className="text-muted small mb-0">
                    Please reload the page and sign in again to continue.
                  </p>
                </>
              ) : (
                <>
                  <p className="fw-semibold mb-1" id="conn-lost-desc">
                    The connection to the server was lost.
                  </p>

                  {/* Live reconnecting indicator */}
                  <p className="text-muted small mb-4">
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ color: 'var(--color-primary)' }}
                      role="status"
                      aria-hidden="true"
                    />
                    Reconnecting{'.'.repeat(dots)}
                  </p>

                  <p className="text-muted small mb-0">
                    The app will resume automatically once the connection is restored.
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer border-0 justify-content-center pb-4">
              <button
                type="button"
                className={`btn btn-sm ${unrecoverable ? '' : 'btn-outline-secondary'}`}
                style={unrecoverable ? { backgroundColor: 'var(--color-primary)', color: '#fff' } : undefined}
                onClick={onReload}
              >
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
                Reload page
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
